import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "../components/TopNav";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Plus, Loader2, Trash2, Users, X, ShieldCheck, Tag, Calendar, Infinity as InfinityIcon, ArrowLeft,
} from "lucide-react";

const BENEFITS = [
  { value: "free_1_day", label: "1 day Pro" },
  { value: "free_1_week", label: "1 week Pro" },
  { value: "free_1_month", label: "1 month Pro" },
  { value: "free_lifetime", label: "Lifetime Pro" },
  { value: "free_project_credit", label: "1 free cover-project credit" },
];

const AdminCodes = () => {
  const navigate = useNavigate();
  const { profile, loading: profileLoading } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", description: "", benefit_type: "free_1_week", max_uses: "", expires_at: "" });
  const [error, setError] = useState("");
  const [redemptionsFor, setRedemptionsFor] = useState(null);

  const fetchCodes = async () => {
    try {
      const { data } = await api.get("/admin/promo");
      setCodes(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profileLoading) return;
    if (profile && !profile.is_admin) {
      // not admin → bounce
      navigate("/dashboard", { replace: true });
      return;
    }
    if (profile?.is_admin) fetchCodes();
  }, [profile, profileLoading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setCreating(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        description: form.description.trim(),
        benefit_type: form.benefit_type,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      const { data } = await api.post("/admin/promo", payload);
      setCodes((c) => [data, ...c]);
      setForm({ code: "", description: "", benefit_type: "free_1_week", max_uses: "", expires_at: "" });
      setAdding(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not create code");
    } finally {
      setCreating(false);
    }
  };

  const deleteCode = async (id, code) => {
    if (!window.confirm(`Delete code ${code}? Existing redemptions stay (cascade), but the code becomes unusable.`)) return;
    try {
      await api.delete(`/admin/promo/${id}`);
      setCodes((c) => c.filter((x) => x.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (profileLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#534AB7]" />
      </div>
    );
  }

  if (profile && !profile.is_admin) return null;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <TopNav />
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-xs text-[#5F5E5A] hover:text-[#2C2C2A] inline-flex items-center gap-1.5"
          data-testid="admin-back-btn"
        >
          <ArrowLeft className="w-3 h-3" /> Back to dashboard
        </button>

        <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-[#534AB7]">
              <ShieldCheck className="w-3.5 h-3.5" /> Admin
            </span>
            <h1
              className="mt-3 text-4xl tracking-tight"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
            >
              Promo codes
            </h1>
            <p className="text-sm text-[#5F5E5A] mt-1">
              Create launch and partner codes. Track redemptions in one place.
            </p>
          </div>
          <button
            onClick={() => setAdding(true)}
            data-testid="admin-new-code-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] shadow-sm"
          >
            <Plus className="w-4 h-4" /> New code
          </button>
        </div>

        {error && (
          <div className="mt-4 px-4 py-3 rounded-[8px] bg-[#BA7517]/10 border border-[#BA7517]/40 text-sm text-[#BA7517]" data-testid="admin-error">
            {error}
          </div>
        )}

        {adding && (
          <form
            onSubmit={submit}
            className="mt-6 bg-white border border-[#D3D1C7] rounded-[12px] p-6 space-y-4"
            data-testid="admin-code-form"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium uppercase tracking-[0.18em] text-[#5F5E5A]">Create a code</h3>
              <button type="button" onClick={() => setAdding(false)} className="p-1 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Code">
                <input
                  required
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="LAUNCH50"
                  data-testid="admin-code-input"
                  className={inp + " font-mono uppercase"}
                />
              </Field>
              <Field label="Description (internal)">
                <input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Substack launch — first 50"
                  className={inp}
                />
              </Field>
              <Field label="Benefit">
                <select
                  value={form.benefit_type}
                  onChange={(e) => setForm({ ...form, benefit_type: e.target.value })}
                  data-testid="admin-benefit-select"
                  className={inp}
                >
                  {BENEFITS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
              </Field>
              <Field label="Max uses (blank = unlimited)">
                <input
                  type="number"
                  min={1}
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder="50"
                  data-testid="admin-max-uses-input"
                  className={inp}
                />
              </Field>
              <Field label="Expires at (blank = never)">
                <input
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className={inp}
                />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="px-4 py-2 rounded-[8px] border border-[#D3D1C7] text-sm hover:bg-[#F1EFE8]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!form.code.trim() || creating}
                data-testid="admin-create-btn"
                className="px-5 py-2 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50 inline-flex items-center gap-2"
              >
                {creating && <Loader2 className="w-4 h-4 animate-spin" />} Create code
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 bg-white border border-[#D3D1C7] rounded-[12px] overflow-hidden" data-testid="admin-codes-table">
          {codes.length === 0 ? (
            <div className="p-10 text-center">
              <Tag className="w-7 h-7 mx-auto text-[#534AB7]" strokeWidth={1.5} />
              <p className="mt-3 text-sm text-[#5F5E5A]">No promo codes yet. Create your first one above.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FAFAF9] text-left">
                  <Th>Code</Th>
                  <Th>Benefit</Th>
                  <Th className="text-right">Used</Th>
                  <Th>Expires</Th>
                  <Th>Created</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.id} className="border-t border-[#D3D1C7]" data-testid={`code-row-${c.code}`}>
                    <Td>
                      <div className="font-mono uppercase text-[#2C2C2A] font-medium">{c.code}</div>
                      {c.description && <div className="text-[11px] text-[#5F5E5A] mt-0.5">{c.description}</div>}
                    </Td>
                    <Td>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#F1EFE8] text-[#534AB7] text-[11px] uppercase tracking-wider">
                        {(BENEFITS.find((b) => b.value === c.benefit_type) || {}).label || c.benefit_type}
                      </span>
                    </Td>
                    <Td className="text-right tabular-nums">
                      <button
                        onClick={() => setRedemptionsFor(c)}
                        className="hover:underline text-[#534AB7]"
                        title="View redemptions"
                        data-testid={`view-redemptions-${c.code}`}
                      >
                        {c.current_uses || 0}
                      </button>
                      {" "}
                      <span className="text-[#5F5E5A]/60">/ {c.max_uses == null ? "∞" : c.max_uses}</span>
                    </Td>
                    <Td className="text-[#5F5E5A]">
                      {c.expires_at ? (
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(c.expires_at).toLocaleDateString()}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#5F5E5A]/70"><InfinityIcon className="w-3 h-3" /> never</span>
                      )}
                    </Td>
                    <Td className="text-[#5F5E5A]">{new Date(c.created_at).toLocaleDateString()}</Td>
                    <Td className="text-right">
                      <button
                        onClick={() => deleteCode(c.id, c.code)}
                        data-testid={`delete-code-${c.code}`}
                        className="p-1.5 rounded-md text-[#5F5E5A] hover:bg-[#F1EFE8] hover:text-[#BA7517]"
                        aria-label={`Delete ${c.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {redemptionsFor && (
          <RedemptionsDrawer code={redemptionsFor} onClose={() => setRedemptionsFor(null)} />
        )}
      </section>
    </div>
  );
};

const RedemptionsDrawer = ({ code, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/admin/promo/${code.id}/redemptions`).then(({ data }) => setRows(data || [])).finally(() => setLoading(false));
  }, [code.id]);
  return (
    <div className="fixed inset-0 z-50 bg-[#2C2C2A]/55 backdrop-blur-sm flex items-center justify-center px-4 py-6" data-testid="redemptions-drawer">
      <div className="w-full max-w-xl bg-white rounded-[12px] border border-[#D3D1C7] shadow-2xl">
        <div className="px-6 py-4 border-b border-[#D3D1C7] flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A]">Redemptions</div>
            <h3 className="font-mono uppercase font-medium text-[#2C2C2A] mt-0.5">{code.code}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#534AB7]" /></div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#5F5E5A]">
              <Users className="w-6 h-6 mx-auto text-[#534AB7]/50 mb-2" strokeWidth={1.5} />
              No-one has redeemed this code yet.
            </div>
          ) : (
            <ul className="divide-y divide-[#D3D1C7]">
              {rows.map((r) => (
                <li key={r.id} className="px-6 py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="text-[#2C2C2A]">{r.user_name || r.user_email || r.user_id}</div>
                    {r.user_email && r.user_name && <div className="text-[11px] text-[#5F5E5A]">{r.user_email}</div>}
                  </div>
                  <div className="text-[11px] text-[#5F5E5A] tabular-nums">{new Date(r.created_at).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-[#5F5E5A] uppercase tracking-wider mb-1.5">{label}</span>
    {children}
  </label>
);
const Th = ({ children, className = "" }) => (
  <th className={`px-5 py-3 text-xs uppercase tracking-[0.16em] font-medium text-[#5F5E5A] ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => <td className={`px-5 py-4 align-middle ${className}`}>{children}</td>;
const inp = "w-full px-3 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-sm text-[#2C2C2A] focus:outline-none focus:border-[#534AB7]";

export default AdminCodes;
